import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS, scrollToId, site, waLink, WA_MESSAGES } from "../config/site";
import { trackEvent } from "../lib/analytics";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("#inicio");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    NAV_LINKS.forEach((l) => {
      const el = document.querySelector(l.hash);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-12" aria-label="Navegação principal">
        <a href="#inicio" onClick={(e) => go(e, "#inicio")} data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/assets/logo.jpg" alt="Logotipo GOBAX" width={40} height={40} className="h-10 w-10 rounded-full ring-1 ring-white/20" />
          <span className="leading-none">
            <span className="block font-display text-lg font-extrabold tracking-wide">GOBAX</span>
            <span className="block font-mono text-[9px] uppercase tracking-[0.28em] text-gb-gray">{site.tagline}</span>
          </span>
        </a>

        <ul className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.hash}>
              <a
                href={l.hash}
                onClick={(e) => go(e, l.hash)}
                data-testid={`nav-link-${l.hash.slice(1)}`}
                aria-current={active === l.hash ? "true" : undefined}
                className={`nav-link text-sm transition-colors duration-300 hover:text-gb-white ${
                  active === l.hash ? "nav-link-active text-gb-white" : "text-gb-gray"
                }`}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href={waLink(WA_MESSAGES.default)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "navbar" })}
            data-testid="nav-whatsapp-btn"
            className="btn-chrome hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold sm:inline-flex"
          >
            Quero crescer
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-gb-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="glass overflow-hidden border-b border-white/10 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <ul className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((l, i) => (
                <motion.li key={l.hash} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <a
                    href={l.hash}
                    onClick={(e) => go(e, l.hash)}
                    data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                    className={`block py-3 font-display text-xl font-semibold transition-colors ${active === l.hash ? "text-gb-silverhi" : "text-gb-white"}`}
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
              <li className="pt-4">
                <a
                  href={waLink(WA_MESSAGES.default)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "mobile_menu" })}
                  data-testid="nav-mobile-whatsapp-btn"
                  className="btn-chrome inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold"
                >
                  Quero crescer
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
